import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from 'react-query';
import { api } from '../services/api';
import { Wrench, Leaf } from 'lucide-react';
import WorkLine from '../components/crew/WorkLine';

/**
 * The crew lead's day (owner, Sep 29 2026: "Ryan should have a day overview —
 * treatment plans and checklists, if we have bushes, the routes, the client
 * requests — nothing admin side"). One read from /api/lead/day; no prices,
 * no pay, no customer contact info anywhere on this side.
 */
const pad = (n: number) => String(n).padStart(2, '0');
const iso = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const fmtTime = (t?: string | null) => {
  if (!t) return null;
  const [h, m] = String(t).split(':').map(Number);
  return `${h % 12 || 12}:${pad(m)} ${h < 12 ? 'AM' : 'PM'}`;
};
const dayWords = (d: string) => new Date(`${d}T12:00:00`).toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' });

const STATUS: Record<string, string> = {
  complete: 'text-green-600', in_progress: 'text-blue-500', skipped: 'text-amber-600', scheduled: 'text-gray-400',
};
const mark = (s: string) => (s === 'complete' ? '✓' : s === 'in_progress' ? '▶' : s === 'skipped' ? '–' : '□');

function CheckChip({ label, state, to }: { label: string; state: string | null; to: string }) {
  const cls = state === 'complete' ? 'border-green-400 text-green-700' : state === 'running' ? 'border-blue-400 text-blue-600' : 'border-gray-300 text-gray-700';
  return (
    <Link to={to} className={`px-3 py-2 rounded-lg border text-xs font-semibold ${cls}`}>
      {label} {state === 'complete' ? '✓' : state === 'running' ? '· going' : ''}
    </Link>
  );
}

export default function LeadHome() {
  const today = iso(new Date());
  const [day, setDay] = useState(today);
  const shiftDay = (n: number) => { const d = new Date(`${day}T12:00:00`); d.setDate(d.getDate() + n); setDay(iso(d)); };
  const { data, isLoading, isError } = useQuery(['lead-day', day], () => api.get('/lead/day', { params: { date: day } }).then(r => r.data));

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-2xl font-black uppercase tracking-tight text-gray-900">The day</h1>
        <p className="text-xs text-gray-500">Routes, treatments and one-off work.</p>
      </div>

      {/* Which day */}
      <div className="flex items-center gap-2">
        <button onClick={() => shiftDay(-1)} aria-label="Day before" className="px-3 py-2 rounded-lg border border-gray-300 text-sm font-semibold text-gray-700">‹</button>
        <label className="flex-1 relative">
          <span className={`block text-center py-2 rounded-lg border text-sm font-semibold ${day === today ? 'border-gray-300 text-gray-800' : 'border-purple-400 text-purple-700'}`}>{day === today ? `Today · ${dayWords(day)}` : dayWords(day)}</span>
          <input type="date" value={day} onChange={e => e.target.value && setDay(e.target.value)} className="absolute inset-0 opacity-0 cursor-pointer" aria-label="Pick a day" />
        </label>
        <button onClick={() => shiftDay(1)} aria-label="Next day" className="px-3 py-2 rounded-lg border border-gray-300 text-sm font-semibold text-gray-700">›</button>
        {day !== today && <button onClick={() => setDay(today)} className="text-xs font-semibold text-brand-700">Today</button>}
      </div>

      {/* His duties, at a glance */}
      <div className="flex flex-wrap gap-2">
        <CheckChip label="Yard walk" state={data?.checks?.morning ?? null} to="/lead/check" />
        <CheckChip label="Weekly shop list" state={data?.checks?.weekly ?? null} to="/lead/check" />
        <Link to="/mechanic" className="px-3 py-2 rounded-lg border border-gray-300 text-xs font-semibold text-gray-700 flex items-center gap-1">
          <Wrench className="h-3.5 w-3.5" /> The shop
          {(data?.open_gear || 0) > 0 && <span className="ml-1 px-1.5 rounded-full bg-red-100 text-red-700">{data.open_gear}</span>}
          {(data?.open_supplies || 0) > 0 && <span className="ml-1 px-1.5 rounded-full bg-amber-100 text-amber-800">{data.open_supplies}</span>}
        </Link>
      </div>

      {isLoading && <p className="text-sm text-gray-500">Loading…</p>}
      {isError && <p className="text-sm text-red-600">Could not load the day.</p>}

      {/* Routes */}
      {(data?.routes || []).map((r: any) => (
        <div key={r.route_id} className="card p-0 overflow-hidden">
          <div className="px-3 py-2.5 border-b border-gray-100 flex items-baseline justify-between gap-2">
            <div className="min-w-0">
              <p className="text-sm font-bold text-gray-900 truncate">{r.name}</p>
              <p className="text-[11px] text-gray-500">{r.crew.length ? r.crew.join(', ') : 'Nobody assigned yet'}{r.minutes ? ` · ~${Math.round(r.minutes / 60 * 10) / 10}h est` : ''}</p>
            </div>
            <p className="text-xs font-semibold text-gray-600 whitespace-nowrap">{r.done}/{r.total} done{r.skipped ? ` · ${r.skipped} skipped` : ''}</p>
          </div>
          <div className="divide-y divide-gray-50">
            {r.stops.map((s: any, i: number) => (
              <div key={s.id} className="px-3 py-1.5 flex items-center gap-2 text-sm">
                <span className={`w-4 text-center ${STATUS[s.status] || 'text-gray-400'}`}>{mark(s.status)}</span>
                <span className="w-5 text-right text-[11px] text-gray-400">{i + 1}</span>
                {/* The stop, then what to do there: "Mowing", and anything else
                    on the visit as Additional (owner, Sep 30 2026). */}
                <div className="flex-1 min-w-0">
                  <p className="truncate text-gray-900">{s.customer_name || s.work_main || s.title}</p>
                  <WorkLine job={s} showMain={!!s.customer_name} />
                </div>
                {s.treatment && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800"><Leaf className="h-3 w-3 inline -mt-0.5" /> treat</span>}
                <span className={`text-[11px] whitespace-nowrap ${s.firm ? 'text-red-600 font-bold' : 'text-gray-500'}`}>{s.firm ? `⏰ ${fmtTime(s.time)}` : fmtTime(s.eta || s.time) || ''}</span>
              </div>
            ))}
          </div>
        </div>
      ))}
      {data && !data.routes?.length && <p className="card p-4 text-sm text-gray-500">No routes run {day === today ? 'today' : 'that day'}.</p>}

      {/* One-off work: bushes, mulch, projects */}
      {(data?.extras?.length > 0 || data?.loose_mows?.length > 0) && (
        <div className="card p-0 overflow-hidden">
          <p className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wide border-b border-gray-100">Other work {day === today ? 'today' : 'that day'}</p>
          <div className="divide-y divide-gray-50">
            {[...(data?.extras || []), ...(data?.loose_mows || [])].map((s: any) => (
              <div key={s.id} className="px-3 py-2 flex items-center gap-2 text-sm">
                <span className={`w-4 text-center ${STATUS[s.status] || 'text-gray-400'}`}>{mark(s.status)}</span>
                <div className="flex-1 min-w-0">
                  <p className="truncate text-gray-900">{s.work_main || s.kind}{s.customer_name ? ` — ${s.customer_name}` : ''}</p>
                  <WorkLine job={s} showMain={false} />
                  {s.address && <p className="text-[11px] text-gray-500 truncate">{s.address}</p>}
                </div>
                {String(s.kind).toLowerCase() !== String(s.work_main || '').toLowerCase() && (
                  <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-purple-100 text-purple-800">{s.kind}</span>
                )}
                {fmtTime(s.time) && <span className="text-[11px] text-gray-500">{fmtTime(s.time)}</span>}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* To-dos */}
      {(data?.todos?.length || 0) > 0 && (
        <div className="card p-3">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">To-dos</p>
          {data.todos.map((t: any) => (
            <p key={t.id} className="text-sm text-gray-800"><span className={STATUS[t.status] || 'text-gray-400'}>{mark(t.status)}</span> {t.title}{fmtTime(t.time) ? ` · ${fmtTime(t.time)}` : ''}</p>
          ))}
        </div>
      )}

    </div>
  );
}
